import { PopulatedAssessment } from '../../types/app/populated'
import { Assessment, PopulatedPregnancyPlans } from '../../types/assessments/assessment'
import {
  BaseInsuranceCoverage,
  CoverageType,
  InsuranceCoverageRequest,
} from '../../types/insurance/coverage'
import { InsuranceInfo, InsuranceInfoOption, User } from '../../types/users/user'
import { FieldMapValue } from '../../types/utils/common'
import { getCoverageText, getInsurancePlan, getPlanNextAction } from '../insurance'
import { getCorrectedValue } from '../populated'

export const canHaveSecondaryOptions: InsuranceInfoOption[] = [
  'two-plans',
  // 'coverage-will-change',
  'multiple-options',
]

// const coverageWillChangePrimaryCoverageOptions: CoverageWillChangeOption[] = [
//   'employment-coverage-will-change',
//   'added-to-another-plan',
// ]

const getMultipleOptionsHasPrimaryCoverage = (insuranceInfoData: Partial<InsuranceInfo>) => {
  const option = insuranceInfoData?.multipleOptionsDescription
  const confirmPartnerPlan = insuranceInfoData?.confirmPartnerPlan
  const planToAssess = insuranceInfoData?.planToAssess

  if (!option) return false
  switch (option) {
    case 'partner-individual':
      return !!confirmPartnerPlan
    case 'partner-employer':
      return !!planToAssess
    default:
      return false
  }
}
const getMultipleOptionsHasSecondaryCoverage = (insuranceInfoData: Partial<InsuranceInfo>) => {
  const option = insuranceInfoData?.multipleOptionsDescription
  const planToAssess = insuranceInfoData?.planToAssess
  if (!option) return false
  switch (option) {
    case 'partner-employer':
      return !!planToAssess
    default:
      return false
  }
}
export const getHasSecondaryCoverage = (insuranceInfoData: Partial<InsuranceInfo>) => {
  const option = insuranceInfoData?.option
  if (!option) return false
  switch (option) {
    case 'two-plans':
      // return !!insuranceInfoData.onePlanIsMedicaid
      return true
    case 'multiple-options':
      return getMultipleOptionsHasSecondaryCoverage(insuranceInfoData)
    default:
      return false
  }
}

export const getHasPrimaryCoverage = (insuranceInfoData: Partial<InsuranceInfo>) => {
  const option = insuranceInfoData?.option
  if (!option) return false
  switch (option) {
    case 'one-plan':
    case 'medicaid':
    case 'two-plans':
      return true
    case 'not-pregnant':
      return insuranceInfoData?.notPregnantOption === 'current-plan'
    case 'coverage-will-change':
      return !!insuranceInfoData?.coverageWillChangeDescription
    case 'multiple-options':
      return getMultipleOptionsHasPrimaryCoverage(insuranceInfoData)
    default:
      return false
  }
}

export const getPregnancyCoverageType = (
  type: 'primary' | 'secondary',
  insuranceInfoData: Partial<InsuranceInfo>,
): CoverageType | null => {
  const hasCoverage =
    type === 'primary'
      ? getHasPrimaryCoverage(insuranceInfoData)
      : getHasSecondaryCoverage(insuranceInfoData)

  if (!hasCoverage) return null
  if (type === 'primary') {
    switch (insuranceInfoData.option) {
      case 'one-plan':
        return 'non-medicaid'
      case 'medicaid':
        return 'medicaid'
      default:
        break
    }
  }
  const existingCoverage =
    insuranceInfoData?.[type === 'primary' ? 'primaryCoverage' : 'secondaryCoverage']
  const existingIsMedicaid = existingCoverage?.isMedicaid
  if (existingIsMedicaid !== null && existingIsMedicaid !== undefined) {
    return existingIsMedicaid ? 'medicaid' : 'non-medicaid'
  }
  return null
}

export const getPrimaryIsMedicaid = (insuranceInfoData: Partial<InsuranceInfo>) => {
  const hasPrimaryCoverage = getHasPrimaryCoverage(insuranceInfoData)
  if (!hasPrimaryCoverage) return null
  return getPregnancyCoverageType('primary', insuranceInfoData) === 'medicaid'
}

export const getSecondaryIsMedicaid = (insuranceInfoData: Partial<InsuranceInfo>) =>
  getPregnancyCoverageType('secondary', insuranceInfoData) === 'medicaid'

export const getOnlyOnMedicaidPlan = (data?: FieldMapValue) =>
  data?.insuranceInfo?.option === 'medicaid'

export const getPrimaryCoverage = (data?: FieldMapValue): BaseInsuranceCoverage | null => {
  const coverage = data?.insuranceInfo?.primaryCoverage
  if (!coverage) return null
  return {
    ...coverage,
    isMedicaid: false,
  }
}

export const getMedicaidCoverage = (data?: FieldMapValue): BaseInsuranceCoverage | null => {
  const coverage = data?.insuranceInfo?.medicaidCoverage
  if (!coverage) return null
  return {
    ...coverage,
    isMedicaid: true,
  }
}

export const getSecondaryCoverage = (data?: FieldMapValue) => data?.insuranceInfo?.secondaryCoverage

export interface LegacyInsurancePlans {
  additionalPlans: Record<string, BaseInsuranceCoverage>
  requests: Record<string, InsuranceCoverageRequest>
  mainCoverage: BaseInsuranceCoverage | null
  secondaryCoverage: BaseInsuranceCoverage | null
  secondaryOldId: 'primaryCoverage' | 'medicaidCoverage' | `additionalPlans.${string}` | null
}

export const getSnippetIsMarketplacePlan = (assessment: PopulatedAssessment) => {
  const isMedicaidPlan = getPrimaryIsMedicaid(assessment.mergedData?.insuranceInfo ?? {})
  const planOption = getCorrectedValue(assessment, 'insuranceInfo.option')
  return !isMedicaidPlan && planOption !== undefined
}

export const getCoverageConfirmationRequired = (
  assessment: Assessment | PopulatedAssessment | undefined | null,
) => {
  const {
    additionalPlansConfirmedBy,
    additionalPlansConfirmedOn,
    additionalPlansRequestedBy,
    additionalPlansRequestedOn,
    questionnaireSkippedOn,
  } = assessment ?? {}

  if (questionnaireSkippedOn) return false
  if (!additionalPlansConfirmedBy || !additionalPlansConfirmedOn) return true
  // if any plan requires call in and does not have it, return true

  if (additionalPlansRequestedBy && additionalPlansRequestedOn) {
    if (additionalPlansRequestedOn > additionalPlansConfirmedOn) return true
  }

  return !(!additionalPlansRequestedBy || !additionalPlansRequestedOn)
}

export const getPregnancyPrimaryCoverage = (plans: PopulatedPregnancyPlans | null | undefined) =>
  plans?.primary ?? null

export const getPregnancyPrimaryNextAction = (pregnancy: PopulatedAssessment) => {
  if (pregnancy.isGynProfile) return pregnancy.gynNextAction ?? null
  const { nextActions, plans } = pregnancy
  const primaryCoverage = getPregnancyPrimaryCoverage(plans)
  return getPlanNextAction(nextActions ?? {}, primaryCoverage?.id ?? 'primary')
}

export const getPregnancyCoverageText = (
  pregnancy: Assessment,
  plans: PopulatedPregnancyPlans | null,
) => {
  if (!plans) return 'zzz'
  const { data, corrections } = pregnancy ?? {}
  const primary = getPregnancyPrimaryCoverage(plans)
  const option = corrections?.insuranceInfo?.option || data?.insuranceInfo?.option
  if (option === 'no-coverage') return 'No coverage'
  return getCoverageText(primary, primary?.insuranceProvider)
}

export const getPregnancyInsurerIds = (user: User, assessment: Assessment) => {
  const { data, corrections } = assessment ?? {}
  const { primaryCoverage: dataPrimary, secondaryCoverage: dataSecondary } =
    data?.insuranceInfo ?? {}
  const { primaryCoverage: correctionsPrimary, secondaryCoverage: correctionsSecondary } =
    corrections?.insuranceInfo ?? {}
  const insurerIds: string[] = []
  if (dataPrimary?.insuranceProviderId) insurerIds.push(dataPrimary.insuranceProviderId)
  if (dataSecondary?.insuranceProviderId) insurerIds.push(dataSecondary.insuranceProviderId)
  if (correctionsPrimary?.insuranceProviderId)
    insurerIds.push(correctionsPrimary.insuranceProviderId)
  if (correctionsSecondary?.insuranceProviderId)
    insurerIds.push(correctionsSecondary.insuranceProviderId)

  const primaryPlan = assessment.primaryCoverageId
    ? getInsurancePlan(user.insurancePlans ?? {}, assessment.primaryCoverageId)
    : null

  if (primaryPlan?.insuranceProviderId) insurerIds.push(primaryPlan.insuranceProviderId)

  const secondaryPlan = assessment.secondaryCoverageId
    ? getInsurancePlan(user.insurancePlans ?? {}, assessment.secondaryCoverageId)
    : null

  if (secondaryPlan?.insuranceProviderId) insurerIds.push(secondaryPlan.insuranceProviderId)

  assessment.potentialCoverageIds?.forEach(id => {
    const plan = getInsurancePlan(user.insurancePlans ?? {}, id)
    if (plan?.insuranceProviderId) insurerIds.push(plan.insuranceProviderId)
  })

  return Array.from(new Set(insurerIds))
}

export const getInsurerId = (plans: PopulatedPregnancyPlans | null) => {
  if (!plans) return null
  const { primary, secondary } = plans
  return primary?.insuranceProviderId ?? secondary?.insuranceProviderId ?? null
}
